// components/Admin/SellerManagement/SalesManagement/AdminSetPriceCalculator.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toBlob } from 'html-to-image';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../../components/ui/card";
import { Slider } from "../../../../components/ui/slider";
import { Label } from "../../../../components/ui/label";
import { useToast } from "../../../../hooks/use-toast";
// --- IMPORT BOTH API FUNCTIONS ---
import { getSellerStandardWelcomeSets } from '../../../../services/AdminApis'; 
import { getSellerSpecialSetAnalytics } from '../../../../services/AdminSpecialSetApis'; // <-- NEW IMPORT
import { Loader2, Percent, AlertCircle, Coins, Copy } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "../../../../components/ui/alert";
import { Separator } from "../../../../components/ui/separator";
import RegenerateWelcomeSetModal from '../../../../components/Admin/SellerManagement/SalesManagement/RegenerateWelcomeSetModal'; // Import new component
import { Button } from "../../../../components/ui/button"; // Ensure Button is imported

// Helper function to format currency
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount || 0);
};

// --- NEW HELPER for rendering price lines ---
const PriceRow = ({ label, value, isBold = false, valueClassName = "" }) => (
    <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className={isBold ? 'font-medium text-foreground' : 'text-muted-foreground'}>{label}</span>
        <span className={`whitespace-nowrap tabular-nums ${isBold ? 'font-semibold' : ''} ${valueClassName}`}>{value}</span>
    </div>
);

// --- Section heading with an optional discount pill ---
const SectionHeading = ({ title, badge, badgeClassName = "" }) => (
    <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h4>
        {badge && (
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${badgeClassName}`}>
                {badge}
            </span>
        )}
    </div>
);

// --- HEAVILY REFACTORED PackagePriceCard component ---
const PackagePriceCard = ({ packageInfo, newDiscount, avgCoinsPerLead }) => {
    
    
    // Perform all calculations for all three scenarios
    const { baseCalc, websiteCalc, recalc } = useMemo(() => {
        // --- 1. Common Inputs ---
        const undiscountedBasePrice = parseFloat(packageInfo.set_price_before_discount_ex_tax);
        const coinCount = parseFloat(packageInfo.calculated_set_coins);
        const taxRate = parseFloat(packageInfo.tax_rate) / 100; // e.g., 0.18
        const websiteDiscountPercent = parseFloat(packageInfo.discount_percentage);
        const newDiscountPercent = parseFloat(newDiscount);

        // --- 2. Base Price (Undiscounted) Calculations ---
        const basePricePerCoinExGst = coinCount > 0 ? (undiscountedBasePrice / coinCount) : 0;
        const baseGstAmount = undiscountedBasePrice * taxRate;
        const basePriceIncGst = undiscountedBasePrice + baseGstAmount;

        const baseCalc = {
            pricePerCoinExGst: basePricePerCoinExGst,
            priceExGst: undiscountedBasePrice,
            gstAmount: baseGstAmount,
            priceIncGst: basePriceIncGst,
            approxPricePerLeadExGst: basePricePerCoinExGst * avgCoinsPerLead,
        };

        // --- 3. Website Price (Current Discount) Calculations ---
        const websiteDiscountAmount = undiscountedBasePrice * (websiteDiscountPercent / 100);
        const websitePriceExGst = undiscountedBasePrice - websiteDiscountAmount;
        const websitePricePerCoinExGst = coinCount > 0 ? (websitePriceExGst / coinCount) : 0;
        const websiteGstAmount = websitePriceExGst * taxRate;
        const websitePriceIncGst = websitePriceExGst + websiteGstAmount;
        const websiteApproxPricePerLeadExGst = websitePricePerCoinExGst * avgCoinsPerLead;
        const websiteSavingsPerLead = baseCalc.approxPricePerLeadExGst - websiteApproxPricePerLeadExGst;

        const websiteCalc = {
            discount: websiteDiscountPercent,
            pricePerCoinExGst: websitePricePerCoinExGst,
            priceExGst: websitePriceExGst,
            gstAmount: websiteGstAmount, // Not displayed, but good to have
            priceIncGst: websitePriceIncGst,
            approxPricePerLeadExGst: websiteApproxPricePerLeadExGst,
            savingsPerLead: websiteSavingsPerLead
        };

        // --- 4. Recalculated (Admin's New Discount) Calculations ---
        const newDiscountAmount = undiscountedBasePrice * (newDiscountPercent / 100);
        const newPriceExGst = undiscountedBasePrice - newDiscountAmount;
        const newPricePerCoinExGst = coinCount > 0 ? (newPriceExGst / coinCount) : 0;
        const newGstAmount = newPriceExGst * taxRate;
        const newPriceIncGst = newPriceExGst + newGstAmount;
        const newApproxPricePerLeadExGst = newPricePerCoinExGst * avgCoinsPerLead;
        const newSavingsPerLead = baseCalc.approxPricePerLeadExGst - newApproxPricePerLeadExGst;

        const recalc = {
            discount: newDiscountPercent,
            pricePerCoinExGst: newPricePerCoinExGst,
            priceExGst: newPriceExGst,
            gstAmount: newGstAmount,
            priceIncGst: newPriceIncGst,
            approxPricePerLeadExGst: newApproxPricePerLeadExGst,
            savingsPerLead: newSavingsPerLead,
        };

        return { baseCalc, websiteCalc, recalc };

    }, [packageInfo, newDiscount, avgCoinsPerLead]);

    const hasLeadData = avgCoinsPerLead > 0;
    const extraSavingsIncGst = websiteCalc.priceIncGst - recalc.priceIncGst;

    return (
        <Card className="flex h-full flex-col overflow-hidden border border-gray-200 shadow-md">
            {/* --- Header band --- */}
            <div className="bg-brand-deep-navy px-5 py-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-widest text-brand-gold-accent">
                            {packageInfo.package_type.toLowerCase()} set
                        </p>
                        <h3 className="text-lg font-bold leading-snug text-white">
                            {packageInfo.package_name}
                        </h3>
                    </div>
                    <span className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-white/10 px-3 py-1.5 text-sm font-bold text-white">
                        <Coins className="h-4 w-4 text-brand-gold-accent" />
                        {packageInfo.calculated_set_coins}
                        <span className="text-xs font-normal text-white/70">coins</span>
                    </span>
                </div>
            </div>

            <CardContent className="flex flex-1 flex-col space-y-4 p-5">

                {/* --- Section 1: Base Price --- */}
                <div>
                    <SectionHeading
                        title="Base Price"
                        badge="No Discount"
                        badgeClassName="bg-gray-100 text-gray-600"
                    />
                    <div className="space-y-1">
                        <PriceRow label="Price ex. GST" value={formatCurrency(baseCalc.priceExGst)} />
                        <PriceRow label={`GST (${packageInfo.tax_rate}%)`} value={formatCurrency(baseCalc.gstAmount)} />
                        <PriceRow label="Price per coin ex. GST" value={formatCurrency(baseCalc.pricePerCoinExGst)} />
                        {hasLeadData && (
                            <PriceRow label="Approx. price per lead ex. GST" value={formatCurrency(baseCalc.approxPricePerLeadExGst)} />
                        )}
                        <PriceRow label="Price inc. GST" value={formatCurrency(baseCalc.priceIncGst)} isBold={true} />
                    </div>
                </div>

                <Separator />

                {/* --- Section 2: Website Price --- */}
                <div>
                    <SectionHeading
                        title="Website Price"
                        badge={`${websiteCalc.discount}% off`}
                        badgeClassName="bg-blue-50 text-brand-trust-blue"
                    />
                    <div className="space-y-1">
                        <PriceRow label="Price ex. GST" value={formatCurrency(websiteCalc.priceExGst)} />
                        <PriceRow label="Price per coin ex. GST" value={formatCurrency(websiteCalc.pricePerCoinExGst)} />
                        {hasLeadData && (
                            <>
                                <PriceRow label="Approx. price per lead ex. GST" value={formatCurrency(websiteCalc.approxPricePerLeadExGst)} />
                                <PriceRow
                                    label="Savings per lead ex. GST"
                                    value={formatCurrency(websiteCalc.savingsPerLead)}
                                    valueClassName="text-brand-growth-green font-medium"
                                />
                            </>
                        )}
                        <PriceRow label="Price inc. GST" value={formatCurrency(websiteCalc.priceIncGst)} isBold={true} />
                    </div>
                </div>

                {/* --- Section 3: Special Seller Price (hero) --- */}
                <div className="mt-auto overflow-hidden rounded-xl border-2 border-brand-gold-accent shadow-sm">
                    <div className="flex items-center justify-between gap-2 bg-brand-deep-navy px-4 py-2.5">
                        <h4 className="text-sm font-bold text-white">Special Seller Price</h4>
                        <span className="whitespace-nowrap rounded-full bg-brand-gold-accent px-2.5 py-0.5 text-xs font-bold text-brand-deep-navy">
                            {recalc.discount}% OFF
                        </span>
                    </div>
                    <div className="space-y-3 bg-gradient-to-b from-amber-50 to-white p-4">
                        <div className="flex items-end justify-between gap-3">
                            <div>
                                <p className="text-xs text-muted-foreground">Your price (inc. GST)</p>
                                <p className="text-2xl font-extrabold tabular-nums text-brand-deep-navy">
                                    {formatCurrency(recalc.priceIncGst)}
                                </p>
                            </div>
                            {extraSavingsIncGst > 0.005 && (
                                <div className="text-right">
                                    <p className="text-xs tabular-nums text-muted-foreground line-through">
                                        {formatCurrency(websiteCalc.priceIncGst)}
                                    </p>
                                    <p className="text-xs font-semibold text-brand-growth-green">
                                        Save {formatCurrency(extraSavingsIncGst)} vs website
                                    </p>
                                </div>
                            )}
                        </div>
                        <Separator />
                        <div className="space-y-1">
                            <PriceRow label="Price ex. GST" value={formatCurrency(recalc.priceExGst)} isBold={true} />
                            <PriceRow label={`GST (${packageInfo.tax_rate}%)`} value={formatCurrency(recalc.gstAmount)} />
                            <PriceRow label="Price per coin ex. GST" value={formatCurrency(recalc.pricePerCoinExGst)} />
                            {hasLeadData && (
                                <>
                                    <PriceRow label="Approx. price per lead ex. GST" value={formatCurrency(recalc.approxPricePerLeadExGst)} />
                                    <PriceRow
                                        label="Savings per lead ex. GST"
                                        value={formatCurrency(recalc.savingsPerLead)}
                                        valueClassName="text-brand-growth-green font-medium"
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

// --- UPDATED Main Calculator Component ---
const AdminSetPriceCalculator = ({ sellerProfileId }) => {
    const packageCardsRef = useRef(null);
    const [packages, setPackages] = useState(null);
    const [analytics, setAnalytics] = useState(null); // <-- NEW STATE
    const [error, setError] = useState("");
    const [newDiscount, setNewDiscount] = useState(0); 
    const [isCopying, setIsCopying] = useState(false);
    const [selectedPackageIds, setSelectedPackageIds] = useState([]);
    const { toast } = useToast();
    const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);


    // --- UPDATED useEffect to fetch BOTH packages and analytics ---
    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setPackages(null);
                setAnalytics(null);
                setError("");
                setSelectedPackageIds([]);

                const packagesData = await getSellerStandardWelcomeSets(sellerProfileId);
                const fetchedPackages = Array.isArray(packagesData) ? packagesData : [];
                const hasNoPackages = fetchedPackages.length === 0;

                setPackages(fetchedPackages);
                setSelectedPackageIds(fetchedPackages.map(pkg => pkg.id));
                console.log("Set packages:", fetchedPackages)

                if (hasNoPackages) {
                    toast({ variant: "default", title: "No Packages", description: "This seller has no active Welcome or Standard sets." });
                    return;
                }

                const analyticsData = await getSellerSpecialSetAnalytics(sellerProfileId); // <-- Fetch analytics
                setAnalytics(analyticsData); // <-- Save analytics
            } catch (error) {
                setError("Could not load seller's data.");
                setPackages([]);
                setSelectedPackageIds([]);
                toast({ variant: "destructive", title: "Error", description: "Could not load seller's data." });
            }
        };
        fetchAllData();
    }, [sellerProfileId, toast]);

    const avgCoinsPerLead = analytics ? parseFloat(analytics.avg_coins_per_lead) : 0;
    const hasLoadedPackages = Array.isArray(packages);
    const hasNoStandardOrWelcomePackages = hasLoadedPackages && packages.length === 0;
    const hasWelcomePackage = hasLoadedPackages && packages.some(pkg => pkg?.package_type === 'WELCOME');
    const canChangeWelcomeSetCategory = hasNoStandardOrWelcomePackages || hasWelcomePackage;
    const selectedPackages = hasLoadedPackages
        ? packages.filter(pkg => selectedPackageIds.includes(pkg.id))
        : [];

    const togglePackageSelection = (packageId) => {
        setSelectedPackageIds(currentIds => (
            currentIds.includes(packageId)
                ? currentIds.filter(id => id !== packageId)
                : [...currentIds, packageId]
        ));
    };

    const copyPackageCardsAsImage = async () => {
        if (!packageCardsRef.current || selectedPackages.length === 0) return;

        if (!navigator.clipboard?.write || !window.ClipboardItem) {
            toast({
                variant: "destructive",
                title: "Copy Not Supported",
                description: "This browser does not support copying images to the clipboard.",
            });
            return;
        }

        try {
            setIsCopying(true);

            const captureNode = packageCardsRef.current;
            const captureWidth = Math.ceil(captureNode.scrollWidth);
            const captureHeight = Math.ceil(captureNode.scrollHeight)+40;

            const imageBlob = toBlob(captureNode, {
                backgroundColor: '#ffffff',
                width: captureWidth,
                height: captureHeight,
                pixelRatio: 2,
                cacheBust: true,
                style: {
                    position: 'static',
                    left: '0',
                    top: '0',
                    overflow: 'visible',
                },
            }).then((blob) => {
                if (!blob) {
                    throw new Error('Could not create an image of the package cards.');
                }
                return blob;
            });

            await navigator.clipboard.write([
                new ClipboardItem({ 'image/png': imageBlob }),
            ]);

            toast({
                title: "Copied as Image",
                description: "The package cards are ready to paste from your clipboard.",
            });
        } catch (copyError) {
            console.error('Could not copy package cards as an image:', copyError);
            toast({
                variant: "destructive",
                title: "Copy Failed",
                description: "Could not copy the package cards. Use HTTPS or localhost and try again.",
            });
        } finally {
            setIsCopying(false);
        }
    };

    if (!hasLoadedPackages) {
        return <div className="flex justify-center items-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;
    }

    return (
        <div className="p-4 md:p-6">
            <Card className="bg-white shadow-lg border border-gray-200">
                <CardHeader>
                    <CardTitle>Standard & Welcome Set Price Calculator</CardTitle>
                    <CardDescription>
                        Use this tool to calculate package prices with a new discount.
                        This is for estimation and screenshots only; it does not save any changes.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Error</AlertTitle>
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {hasNoStandardOrWelcomePackages ? (
                        <div className="space-y-4">
                            <Alert>
                                <AlertCircle className="h-4 w-4" />
                                <AlertTitle>No Standard or Welcome Packages</AlertTitle>
                                <AlertDescription>
                                    No active Welcome or Standard packages were found for this seller.
                                </AlertDescription>
                            </Alert>
                            <div className="flex justify-end bg-white p-4 rounded-lg border shadow-sm">
                                <div className="flex flex-col gap-2 md:flex-row md:items-center w-full justify-between">
                                    <div>
                                        <h3 className="font-semibold text-lg">Create the Welcome Set</h3>
                                        <p className="text-sm text-gray-500">
                                            Select the seller's primary category to generate their initial Welcome Set.
                                        </p>
                                    </div>
                                    <Button 
                                        variant="secondary" 
                                        className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                                        onClick={() => setIsRegenerateModalOpen(true)}
                                    >
                                        Create Welcome Set
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Discount Slider */}
                            <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                                <Label htmlFor="discount-slider" className="text-lg font-semibold flex justify-between">
                                    <span>Set New Discount</span>
                                    <span className="text-2xl font-bold text-blue-600 flex items-center">
                                        {newDiscount}<Percent className="h-6 w-6 ml-1" />
                                    </span>
                                </Label>
                                <Slider
                                    id="discount-slider"
                                    min={0}
                                    max={50}
                                    step={1}
                                    value={[newDiscount]}
                                    // --- THIS IS THE FIX ---
                                    onValueChange={(val) => setNewDiscount(val[0])} 
                                    // --- END FIX ---
                                />
                                {newDiscount > 30 && (
                                    <Alert variant="destructive">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertTitle>High Discount Warning</AlertTitle>
                                        <AlertDescription>
                                            Discounts over 30% are considered high. Please ensure you have approval. Max 50%.
                                        </AlertDescription>
                                    </Alert>
                                )}
                            </div>

                            {/* Package Cards */}
                            
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 bg-white">
                                {packages.map(pkg => (
                                    <PackagePriceCard 
                                        key={pkg.id}
                                        packageInfo={pkg}
                                        newDiscount={newDiscount}
                                        avgCoinsPerLead={avgCoinsPerLead} // <-- Pass analytics data
                                    />
                                ))}
                            </div>
                            <div
                                ref={packageCardsRef}
                                aria-hidden="true"
                                className="fixed top-0 grid gap-4 bg-white p-2"
                                style={{
                                    left: '-10000px',
                                    width: `${(selectedPackages.length * 360) + (Math.max(0, selectedPackages.length - 1) * 16) + 16}px`,
                                    gridTemplateColumns: `repeat(${Math.max(1, selectedPackages.length)}, minmax(0, 360px))`,
                                    pointerEvents: 'none',
                                }}
                            >
                                {selectedPackages.map(pkg => (
                                    <PackagePriceCard
                                        key={pkg.id}
                                        packageInfo={pkg}
                                        newDiscount={newDiscount}
                                        avgCoinsPerLead={avgCoinsPerLead}
                                    />
                                ))}
                            </div>
                            <div className="space-y-3 rounded-lg border bg-gray-50 p-4">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                        <h4 className="font-semibold">Choose cards to copy</h4>
                                        <p className="text-sm text-muted-foreground">
                                            {selectedPackages.length} of {packages.length} selected
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        {packages.map(pkg => (
                                            <label
                                                key={pkg.id}
                                                className="flex cursor-pointer items-center gap-2 rounded-md border bg-white px-3 py-2 text-sm"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedPackageIds.includes(pkg.id)}
                                                    onChange={() => togglePackageSelection(pkg.id)}
                                                    className="h-4 w-4 accent-blue-600"
                                                />
                                                <span>{pkg.package_name}</span>
                                            </label>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setSelectedPackageIds(packages.map(pkg => pkg.id))}
                                        >
                                            Select All
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setSelectedPackageIds([])}
                                        >
                                            Clear
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <Button
                                    type="button"
                                    onClick={copyPackageCardsAsImage}
                                    disabled={isCopying || selectedPackages.length === 0}
                                >
                                    {isCopying ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <Copy className="mr-2 h-4 w-4" />
                                    )}
                                    {isCopying
                                        ? 'Copying...'
                                        : `Copy ${selectedPackages.length} Selected ${selectedPackages.length === 1 ? 'Card' : 'Cards'}`}
                                </Button>
                            </div>
                        </>
                    )}
                    {!hasNoStandardOrWelcomePackages && canChangeWelcomeSetCategory && <div className="flex justify-end bg-white p-4 rounded-lg border shadow-sm">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center w-full justify-between">
                            <div>
                                <h3 className="font-semibold text-lg">Coins not matching seller Requirement? Calibrate the Welcome Set</h3>
                                <p className="text-sm text-gray-500">
                                    Use this if the seller's initial Welcome Set coins do not match their actual primary category.
                                </p>
                            </div>
                            <Button 
                                variant="secondary" 
                                className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                                onClick={() => setIsRegenerateModalOpen(true)}
                            >
                                Change Welcome Set Category
                            </Button>
                        </div>
                    </div>}
                </CardContent>
                
            </Card>
            <RegenerateWelcomeSetModal 
                isOpen={isRegenerateModalOpen}
                onClose={() => setIsRegenerateModalOpen(false)}
                sellerId={sellerProfileId}
                onSuccess={() => {
                    // Optional: Refresh any data if needed, like the price calculator view
                    window.location.reload(); // Simple reload to ensure all price cards update
                }}
            />
        </div>    
    );
};

export default AdminSetPriceCalculator;
